import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface TrainingStats {
  totalPositions: number;
  totalAttempts: number;
  totalMastered: number;
  currentStreak: number;
  longestStreak: number;
  totalTimeSpentHours: number;
  overallSuccessRate: number;
  successByMistakeType: Record<string, { attempts: number; correct: number; rate: number }>;
  successByCategory: Record<string, { attempts: number; correct: number; rate: number }>;
  weeklyProgress: Array<{
    week: string;
    attempts: number;
    successRate: number;
    avgTime: number;
  }>;
  masteryDistribution: Record<number, number>;
  dailyActivity: Array<{
    date: string;
    attempts: number;
    correct: number;
    timeSpent: number;
  }>;
  recentSessions: Array<{
    sessionId: string;
    date: string;
    positionsAttempted: number;
    correctMoves: number;
    totalTime: number;
  }>;
}

export function useTrainingStats() {
  const [stats, setStats] = useState<TrainingStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const calculateStreak = (dailyActivity: Array<{ date: string }>) => {
    const sorted = [...dailyActivity].sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    
    let currentStreak = 0;
    let longestStreak = 0;
    let tempStreak = 0;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < sorted.length; i++) {
      const date = new Date(sorted[i].date);
      date.setHours(0, 0, 0, 0);
      const daysDiff = Math.floor((today.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysDiff === i) {
        currentStreak++;
      } else {
        break;
      }
    }
    
    for (let i = 0; i < sorted.length - 1; i++) {
      const date1 = new Date(sorted[i].date);
      const date2 = new Date(sorted[i + 1].date);
      const daysDiff = Math.floor((date1.getTime() - date2.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysDiff === 1) {
        tempStreak++;
        longestStreak = Math.max(longestStreak, tempStreak);
      } else {
        tempStreak = 0;
      }
    }
    
    return { 
      current: currentStreak, 
      longest: Math.max(longestStreak + 1, currentStreak) 
    };
  };

  const loadStats = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get overall stats
      const { data: positions, error: posError } = await (supabase as any)
        .from('training_positions')
        .select('*, training_attempts(*)')
        .eq('user_id', user.id)
        .eq('is_archived', false);

      if (posError) throw posError;

      const totalPositions = positions?.length || 0;
      const totalMastered = positions?.filter((p: any) => p.mastery_level >= 4).length || 0;
      
      const allAttempts = positions?.flatMap((p: any) => p.training_attempts || []) || [];
      const totalAttempts = allAttempts.length;
      const correctAttempts = allAttempts.filter((a: any) => a.is_correct).length;
      const overallSuccessRate = totalAttempts > 0 ? Math.round((correctAttempts / totalAttempts) * 100) : 0;
      
      const totalTimeSeconds = allAttempts.reduce((sum: number, a: any) => sum + (a.time_spent_seconds || 0), 0);
      const totalTimeSpentHours = Math.round((totalTimeSeconds / 3600) * 10) / 10;

      // Success by mistake type
      const successByMistakeType: Record<string, { attempts: number; correct: number; rate: number }> = {};
      positions?.forEach((pos: any) => {
        const type = pos.mistake_type || 'unknown';
        if (!successByMistakeType[type]) {
          successByMistakeType[type] = { attempts: 0, correct: 0, rate: 0 };
        }
        const posAttempts = pos.training_attempts || [];
        successByMistakeType[type].attempts += posAttempts.length;
        successByMistakeType[type].correct += posAttempts.filter((a: any) => a.is_correct).length;
      });
      
      Object.keys(successByMistakeType).forEach(type => {
        const data = successByMistakeType[type];
        data.rate = data.attempts > 0 ? Math.round((data.correct / data.attempts) * 100) : 0;
      });

      // Success by category
      const successByCategory: Record<string, { attempts: number; correct: number; rate: number }> = {};
      positions?.forEach((pos: any) => {
        const category = pos.position_category || 'unknown';
        if (!successByCategory[category]) {
          successByCategory[category] = { attempts: 0, correct: 0, rate: 0 };
        }
        const posAttempts = pos.training_attempts || [];
        successByCategory[category].attempts += posAttempts.length;
        successByCategory[category].correct += posAttempts.filter((a: any) => a.is_correct).length;
      });
      
      Object.keys(successByCategory).forEach(category => {
        const data = successByCategory[category];
        data.rate = data.attempts > 0 ? Math.round((data.correct / data.attempts) * 100) : 0;
      });

      // Weekly progress (last 12 weeks)
      const weeklyMap = new Map<string, { attempts: number; correct: number; totalTime: number }>();
      allAttempts.forEach((attempt: any) => {
        const date = new Date(attempt.created_at);
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        const weekKey = weekStart.toISOString().split('T')[0];
        
        if (!weeklyMap.has(weekKey)) {
          weeklyMap.set(weekKey, { attempts: 0, correct: 0, totalTime: 0 });
        }
        const weekData = weeklyMap.get(weekKey)!;
        weekData.attempts++;
        if (attempt.is_correct) weekData.correct++;
        weekData.totalTime += attempt.time_spent_seconds || 0;
      });

      const weeklyProgress = Array.from(weeklyMap.entries())
        .map(([week, data]) => ({
          week,
          attempts: data.attempts,
          successRate: Math.round((data.correct / data.attempts) * 100),
          avgTime: Math.round(data.totalTime / data.attempts)
        }))
        .sort((a, b) => a.week.localeCompare(b.week))
        .slice(-12);

      // Mastery distribution
      const masteryDistribution: Record<number, number> = {};
      positions?.forEach((pos: any) => {
        const level = pos.mastery_level || 0;
        masteryDistribution[level] = (masteryDistribution[level] || 0) + 1;
      });

      // Daily activity (last 12 months)
      const dailyMap = new Map<string, { attempts: number; correct: number; timeSpent: number }>();
      allAttempts.forEach((attempt: any) => {
        const date = new Date(attempt.created_at).toISOString().split('T')[0];
        if (!dailyMap.has(date)) {
          dailyMap.set(date, { attempts: 0, correct: 0, timeSpent: 0 });
        }
        const dayData = dailyMap.get(date)!;
        dayData.attempts++;
        if (attempt.is_correct) dayData.correct++;
        dayData.timeSpent += attempt.time_spent_seconds || 0;
      });

      const dailyActivity = Array.from(dailyMap.entries())
        .map(([date, data]) => ({
          date,
          attempts: data.attempts,
          correct: data.correct,
          timeSpent: data.timeSpent
        }))
        .sort((a, b) => b.date.localeCompare(a.date));

      const streaks = calculateStreak(dailyActivity);

      // Recent sessions (group by session_id)
      const sessionMap = new Map<string, { date: string; attempts: number; correct: number; totalTime: number }>();
      allAttempts.forEach((attempt: any) => {
        const sessionId = attempt.session_id || 'unknown';
        if (!sessionMap.has(sessionId)) {
          sessionMap.set(sessionId, {
            date: attempt.created_at,
            attempts: 0,
            correct: 0,
            totalTime: 0
          });
        }
        const session = sessionMap.get(sessionId)!;
        session.attempts++;
        if (attempt.is_correct) session.correct++;
        session.totalTime += attempt.time_spent_seconds || 0;
      });

      const recentSessions = Array.from(sessionMap.entries())
        .map(([sessionId, data]) => ({
          sessionId,
          date: data.date,
          positionsAttempted: data.attempts,
          correctMoves: data.correct,
          totalTime: data.totalTime
        }))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 10);

      setStats({
        totalPositions,
        totalAttempts,
        totalMastered,
        currentStreak: streaks.current,
        longestStreak: streaks.longest,
        totalTimeSpentHours,
        overallSuccessRate,
        successByMistakeType,
        successByCategory,
        weeklyProgress,
        masteryDistribution,
        dailyActivity,
        recentSessions
      });

    } catch (error) {
      console.error('Error loading training stats:', error);
      toast({
        title: 'Error',
        description: 'Failed to load training statistics',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getImprovementTrend = useCallback(() => {
    if (!stats || stats.weeklyProgress.length < 2) return null;
    
    const recent = stats.weeklyProgress.slice(-2);
    const last = recent[recent.length - 1];
    const previous = recent[recent.length - 2];
    
    const trend = last.successRate - previous.successRate;
    return {
      trend,
      isImproving: trend > 0,
      recentRate: last.successRate,
      previousRate: previous.successRate
    };
  }, [stats]);

  const getWeakestAreas = useCallback(() => {
    if (!stats) return [];
    
    const areas = [
      ...Object.entries(stats.successByMistakeType).map(([type, data]) => ({
        name: type,
        type: 'mistake',
        ...data
      })),
      ...Object.entries(stats.successByCategory).map(([category, data]) => ({
        name: category,
        type: 'category',
        ...data
      }))
    ];
    
    return areas
      .filter(a => a.attempts >= 5)
      .sort((a, b) => a.rate - b.rate)
      .slice(0, 3);
  }, [stats]);

  const getStrongestAreas = useCallback(() => {
    if (!stats) return [];
    
    const areas = [
      ...Object.entries(stats.successByCategory).map(([category, data]) => ({
        name: category,
        ...data
      }))
    ];
    
    return areas
      .filter(a => a.attempts >= 5)
      .sort((a, b) => b.rate - a.rate)
      .slice(0, 3);
  }, [stats]);

  return {
    stats,
    isLoading,
    loadStats,
    getImprovementTrend,
    getWeakestAreas,
    getStrongestAreas
  };
}
